import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MasterSchedulerJob,
  MasterSchedulerJobType,
  MasterSchedulerLastRunStatus,
} from '../../api/command-dashboard-api';
import { pagesConfig } from '../../shared/pages-config';
import { dayjsWithExtensions as dayjs } from '../../utils/dayjs-config';
import TileInsufficientAccessNotice from './tile-insufficient-access-notice';
import type { CommandDashboardTileBodyProps } from './tile-content-registry';
import { useCanSeeTile } from './use-tile-access';
import { useMasterSchedulerTileMetrics } from './use-master-scheduler-tile-metrics';
import { useBindTileRefresh } from './use-tile-refresh';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';

const JOB_TYPE_NAVIGATION_LINK: Record<MasterSchedulerJobType, string> = {
  report: pagesConfig.reports.link,
  signature: pagesConfig.signatureTemplates.link,
  policy: pagesConfig.policies.link,
  contactSync: pagesConfig.contactSync.link,
};

const STATUS_LABEL: Record<MasterSchedulerLastRunStatus, string> = {
  PENDING: 'Pending',
  RUNNING: 'Running',
  SUCCESSFUL: 'Successful',
  FAILED: 'Failed',
};

// Running work is the most actionable, followed by failures. Pending work sits ahead of completed
// work so successful jobs remain the final group. Every group is then ordered by its soonest next
// run, with jobs that have no upcoming run at the end of that group.
const DEFAULT_STATUS_ORDER: Record<MasterSchedulerLastRunStatus, number> = {
  RUNNING: 0,
  FAILED: 1,
  PENDING: 2,
  SUCCESSFUL: 3,
};

const PAGE_SIZE = 5;

type SortKey = 'jobName' | 'nextRunTime' | 'lastRunStatus';
type SortDirection = 'asc' | 'desc';

function formatNextRunTime(nextRunTime: number | null): string {
  if (nextRunTime === null) {
    return 'No upcoming run';
  }
  const runTime = dayjs(nextRunTime);
  const dayLabel = runTime.isSame(dayjs(), 'day')
    ? 'Today'
    : runTime.format('MMM D');
  return `${dayLabel}, ${runTime.format('h:mm A')}`;
}

function compareNextRunTime(
  left: MasterSchedulerJob,
  right: MasterSchedulerJob
) {
  return (
    (left.nextRunTime ?? Number.POSITIVE_INFINITY) -
    (right.nextRunTime ?? Number.POSITIVE_INFINITY)
  );
}

function compareJobs(
  left: MasterSchedulerJob,
  right: MasterSchedulerJob,
  sortKey: SortKey,
  direction: SortDirection
) {
  let result: number;
  if (sortKey === 'jobName') {
    result = left.jobName.localeCompare(right.jobName);
  } else if (sortKey === 'nextRunTime') {
    result = compareNextRunTime(left, right);
  } else {
    result =
      DEFAULT_STATUS_ORDER[left.lastRunStatus] -
      DEFAULT_STATUS_ORDER[right.lastRunStatus];
    if (result === 0) {
      result = compareNextRunTime(left, right);
    }
  }

  return direction === 'asc' ? result : -result;
}

const HEADER_CELL_SX = {
  position: 'relative',
  fontWeight: 500,
  fontSize: '14px',
  letterSpacing: '0.17px',
  lineHeight: '24px',
  whiteSpace: 'nowrap',
  pr: 0.5,
  color: (theme: { palette: { semantic: { text: { title2: string } } } }) =>
    theme.palette.semantic.text.title2,
  bgcolor: (theme: { palette: { semantic: { surface: { bg3: string } } } }) =>
    theme.palette.semantic.surface.bg3,
  '&::after': {
    content: '""',
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    height: '24px',
    width: '1px',
    backgroundColor: 'divider',
  },
};

const SORT_LABEL_SX = {
  fontWeight: 500,
  color: 'inherit',
  '&:hover, &:focus, &.Mui-active': { color: 'inherit' },
  '& .MuiTableSortLabel-icon': {
    opacity: 1,
    color: 'inherit',
  },
};

export default function MasterSchedulerTileContent({
  tile,
}: CommandDashboardTileBodyProps) {
  const hasPermission = useCanSeeTile(tile);
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState<SortKey>('lastRunStatus');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(0);

  const {
    jobs,
    successfulJobs,
    jobsCompletedToday,
    jobsFailedLast24h,
    jobsCurrentlyRunning,
    hasLoadedData,
    isFetching,
    onRefresh,
  } = useMasterSchedulerTileMetrics(hasPermission);

  useBindTileRefresh(tile, hasPermission, { onRefresh, isFetching });

  const sortedJobs = useMemo(
    () =>
      [...jobs].sort((left, right) =>
        compareJobs(left, right, sortKey, sortDirection)
      ),
    [jobs, sortDirection, sortKey]
  );

  if (!hasPermission && !hasLoadedData) {
    return <TileInsufficientAccessNotice tile={tile} />;
  }

  const maxPage = Math.max(0, Math.ceil(sortedJobs.length / PAGE_SIZE) - 1);
  const displayedPage = Math.min(page, maxPage);
  const visibleJobs = sortedJobs.slice(
    displayedPage * PAGE_SIZE,
    displayedPage * PAGE_SIZE + PAGE_SIZE
  );

  const changeSort = (nextSortKey: SortKey) => {
    setPage(0);
    if (sortKey === nextSortKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(nextSortKey);
      setSortDirection('asc');
    }
  };

  const metrics = [
    { label: 'Successful Jobs', value: successfulJobs },
    { label: 'Jobs Completed Today', value: jobsCompletedToday },
    { label: 'Jobs Failed (Last 24h)', value: jobsFailedLast24h },
    { label: 'Jobs Currently Running', value: jobsCurrentlyRunning },
  ];

  return (
    <Box
      data-testid="master-scheduler-metrics"
      sx={{
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        boxSizing: 'border-box',
        pb: 3,
      }}
    >
      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />}
        sx={{
          px: 3,
          py: 1.5,
          minHeight: 80,
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        {metrics.map((metric) => (
          <Stack
            key={metric.label}
            direction="row"
            spacing={3}
            alignItems="center"
            sx={{
              px: 1,
              py: 1,
              flex: '0 1 auto',
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: '16px',
                lineHeight: 1.5,
                letterSpacing: '0.15px',
                color: (theme) => theme.palette.semantic.text.title2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {metric.label}
            </Typography>
            {isFetching ? (
              <Skeleton width={32} />
            ) : (
              <Typography
                sx={{
                  fontSize: '24px',
                  lineHeight: 1.334,
                  color: (theme) => theme.palette.semantic.stepper.active,
                  flexShrink: 0,
                }}
              >
                {metric.value}
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>

      <TableContainer sx={{ px: 3, flexShrink: 0 }}>
        <Table sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow
              sx={{
                height: 56,
                '& .MuiTableCell-root': {
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  '&:first-of-type': { borderTopLeftRadius: '4px' },
                  '&:last-of-type': {
                    borderTopRightRadius: '4px',
                    '&::after': { display: 'none' },
                  },
                },
              }}
            >
              <TableCell sx={{ ...HEADER_CELL_SX, width: '34%' }}>
                <TableSortLabel
                  active={sortKey === 'jobName'}
                  direction={sortKey === 'jobName' ? sortDirection : 'asc'}
                  sx={SORT_LABEL_SX}
                  onClick={(event) => {
                    event.stopPropagation();
                    changeSort('jobName');
                  }}
                >
                  Job Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, width: '34%' }}>
                <TableSortLabel
                  active={sortKey === 'nextRunTime'}
                  direction={sortKey === 'nextRunTime' ? sortDirection : 'asc'}
                  sx={SORT_LABEL_SX}
                  onClick={(event) => {
                    event.stopPropagation();
                    changeSort('nextRunTime');
                  }}
                >
                  Next Scheduled Run Time
                </TableSortLabel>
              </TableCell>
              <TableCell sx={HEADER_CELL_SX}>
                <TableSortLabel
                  active={sortKey === 'lastRunStatus'}
                  direction={
                    sortKey === 'lastRunStatus' ? sortDirection : 'asc'
                  }
                  sx={SORT_LABEL_SX}
                  onClick={(event) => {
                    event.stopPropagation();
                    changeSort('lastRunStatus');
                  }}
                >
                  Last Run Status
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching &&
              Array.from({ length: PAGE_SIZE }).map((_, index) => (
                // eslint-disable-next-line react-x/no-array-index-key -- placeholder rows
                <TableRow key={`skeleton-row-${index}`} sx={{ height: 72 }}>
                  <TableCell>
                    <Skeleton width="70%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton width="65%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton width="45%" />
                  </TableCell>
                </TableRow>
              ))}
            {!isFetching && visibleJobs.length === 0 && (
              <TableRow sx={{ height: 72 }}>
                <TableCell colSpan={3}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'center' }}
                  >
                    No scheduled jobs.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!isFetching &&
              visibleJobs.map((job) => (
                <TableRow
                  key={`${job.jobType}-${job.jobId}`}
                  hover
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/${JOB_TYPE_NAVIGATION_LINK[job.jobType]}`);
                  }}
                  sx={{ height: 72, cursor: 'pointer' }}
                >
                  <TableCell sx={{ pl: 3 }}>
                    <Typography variant="body2" noWrap>
                      {job.jobName}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ pl: 3, whiteSpace: 'nowrap' }}>
                    <Typography variant="body2">
                      {formatNextRunTime(job.nextRunTime)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ pl: 3 }}>
                    <Typography variant="body2">
                      {STATUS_LABEL[job.lastRunStatus]}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ flex: 1 }} />

      <TablePagination
        component="div"
        count={sortedJobs.length}
        page={displayedPage}
        onPageChange={(event, nextPage) => {
          event?.stopPropagation();
          setPage(nextPage);
        }}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[]}
        sx={{
          flexShrink: 0,
          '& .MuiTablePagination-toolbar': { minHeight: 52 },
          '& .MuiTablePagination-displayedRows': {
            fontSize: '12px',
            lineHeight: 1.66,
            letterSpacing: '0.4px',
            color: 'text.primary',
          },
          '& .MuiTablePagination-actions': { ml: '26px' },
        }}
      />
    </Box>
  );
}
